import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import React from 'react';
import InfoIcon from 'wix-ui-icons-common/InfoCircle';
import { DeviceType } from '../../../../types/DeviceType';
import { Experiment } from '../../../../types/Experiment';
import { Theme } from '../../../../types/Theme';
import { DH } from '../../../../utils/DataHook';
import { PaymentMethodLayout } from '../PaymentMethodLayout';
import { CommonMethodProps } from '../types';
import {
  Description,
  Details,
  RedirectIcon,
  Title,
  SmallRedirectIcon,
  SinglePaymentMethodViewIcon,
} from './elements';
import cn from 'classnames';
import * as s from './styles.scss';
import { useAdaptedComponents } from '../../../../hooks/useAdaptedComponents';
import { PaymentMethodIcon } from '../../../PaymentMethodIcon';

export interface WalletProps extends CommonMethodProps {
  amount?: string;
  currency?: string;
  country?: string;
  paymentMethodName: string | PaymentMethod;
  paymentMethodLabel: string;
  paymentMethodIconUrl?: string;
  withTitle?: boolean;
  withRedirect?: boolean;
  deviceType?: DeviceType;
  locale?: string;
}

export const Wallet: React.FunctionComponent<WalletProps> = (props) => {
  const {
    paymentMethodName,
    paymentMethodLabel,
    paymentMethodIconUrl,
    amount,
    currency,
    country,
    deviceType,
    theme,
    withTitle,
    withRedirect,
    isSinglePaymentOption,
    locale,
  } = props;

  const viewWithLogo = experimentsService.isExperimentEnabled(
    Experiment.SinglePaymentMethodViewWithLogo
  );

  const isSinglePaymentViewShown = isSinglePaymentOption && viewWithLogo;

  const AdaptedComponents = useAdaptedComponents();
  if (AdaptedComponents) {
    return (
      <AdaptedComponents.PaymentMethodScreen
        dataHook={DH.PaymentMethod.Wallet.Container(paymentMethodName)}
        title={
          withTitle ? (
            <div data-hook={DH.PaymentMethod.Wallet.Icon}>
              <PaymentMethodIcon
                paymentMethod={paymentMethodName as PaymentMethod}
                url={paymentMethodIconUrl}
                lang={locale}
              />
            </div>
          ) : null
        }
      >
        <div>
          <div className={cn(s.details, s.alignLeft)}>
            <Description
              deviceType={deviceType}
              methodName={paymentMethodName as PaymentMethod}
              amount={amount}
              currency={currency}
              country={country}
              isSinglePaymentMethodOption={isSinglePaymentViewShown}
            />
          </div>
          <div className={cn(s.details, s.alignLeft)}>
            <Details
              method={paymentMethodName}
              label={paymentMethodLabel}
              deviceType={deviceType}
              withRedirect={withRedirect}
            />
          </div>
        </div>
      </AdaptedComponents.PaymentMethodScreen>
    );
  }

  return (
    <PaymentMethodLayout
      dataHook={DH.PaymentMethod.Wallet.Container(paymentMethodName)}
      theme={theme}
    >
      <div className={s.root}>
        {withTitle && isSinglePaymentViewShown && (
          <SinglePaymentMethodViewIcon
            deviceType={deviceType}
            locale={locale}
            paymentMethodIconUrl={paymentMethodIconUrl}
            paymentMethodName={paymentMethodName as PaymentMethod}
          />
        )}
        {!isSinglePaymentViewShown && withTitle && (
          <Title
            method={paymentMethodName}
            label={paymentMethodLabel}
            iconUrl={paymentMethodIconUrl}
            isModal={theme === Theme.modal}
            amount={amount}
            locale={locale}
          />
        )}
        <div
          className={cn(s.description, {
            [s.singlePaymentOptionDescription]: isSinglePaymentViewShown,
            [s.descriptionMobileView]: deviceType === 'mobile',
          })}
        >
          <Description
            deviceType={deviceType}
            methodName={paymentMethodName as PaymentMethod}
            amount={amount}
            currency={currency}
            country={country}
            isSinglePaymentMethodOption={isSinglePaymentViewShown}
          />
        </div>
        {withRedirect ? (
          <div>
            <div
              className={cn(s.details, {
                [s.alignLeft]: isSinglePaymentViewShown,
              })}
            >
              {isSinglePaymentViewShown && <SmallRedirectIcon />}
              <Details
                method={paymentMethodName}
                label={paymentMethodLabel}
                deviceType={deviceType}
                withRedirect={withRedirect}
              />
            </div>
            {!isSinglePaymentViewShown && <RedirectIcon />}
          </div>
        ) : (
          <div
            className={cn(s.details, {
              [s.alignLeft]: !isSinglePaymentOption && theme !== Theme.modal,
            })}
          >
            <InfoIcon size="21" className={s.infoIcon} />
            <Details
              method={paymentMethodName}
              label={paymentMethodLabel}
              deviceType={deviceType}
              withRedirect={withRedirect}
            />
          </div>
        )}
      </div>
    </PaymentMethodLayout>
  );
};
Wallet.displayName = 'Wallet';
